import { Badge, Box, Flex, Icon, IconButton, Text, useDisclosure } from '@chakra-ui/react';
import { format } from 'date-fns';
import debounce from 'lodash/debounce';
import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { IoMdCloseCircle } from 'react-icons/io';
import { CellValue } from 'react-table';

import { IconBan, IconCheckCircle, IconTrashbin, IconView } from 'assets/icons';
import { ControllableDrawer } from 'components/Drawer';
import Table, { IndeterminateCheckbox } from 'components/Table';
import Tooltip from 'components/Tooltip';
import RequestApproveConfirmationModal from 'containers/TimeOff/Modal/RequestApprovalConfirmationModal';
import { initialApproveFormData } from 'containers/TimeOff/Modal/RequestApprovalConfirmationModal/schema';
import RequestRejectConfirmationModal from 'containers/TimeOff/Modal/RequestRejectConfirmationModal';
import RequestWithdrawConfirmationModal from 'containers/TimeOff/Modal/RequestWithdrawConfirmationModal';
import RequestDetails from 'containers/TimeOff/RightDrawer/RequestDetails';
import FilterGroup from 'containers/TimeOff/shared/FilterGroup';
import RequestStatus from 'containers/TimeOff/shared/RequestStatus';
import { roles } from 'global/rolesAndPermissions';
import {
  useFetchPaginatedTimeOffRequests,
  ITimeOffRequestSearchQuery,
} from 'hooks/timeOff/useFetchPaginatedTimeoffRequests';
import { useUserInfo } from 'store/useUserStore';

import RequestsTableFooter from './RequestsTableFooter';

const initialTimeOffRequest: ITimeOffRequestSearchQuery = {
  page: 1,
  page_size: 25,
  search: '',
  sort: '',
  category_ids: '',
  user_ids: '',
  is_approve: '1',
  is_reject: '1',
};

interface IRequestsProps {
  requestType?: 'pending' | 'withoutPending';
}

const Requests: React.FC<IRequestsProps> = ({ requestType }) => {
  const { t } = useTranslation(['timeOff', 'common']);

  const confirmationModalTypeRef = React.useRef<'delete' | 'withdraw'>('delete');

  const [searchQuery, setSearchQuery] = useState<ITimeOffRequestSearchQuery>({
    ...initialTimeOffRequest,
    ...(requestType === 'pending' ? { is_approve: '0', is_reject: '0' } : { is_approve: '1', is_reject: '1' }),
  });
  const [dialogIds, setDialogIds] = useState<{ ids: number[] }>({ ids: [] });
  const [request, setRequestDetails] = useState<any>({});
  const [approveModalFormData, setApproveModalFormData] =
    useState<typeof initialApproveFormData>(initialApproveFormData);

  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const {
    isOpen: isApprovalConfirmationModalOpen,
    onOpen: onApprovalConfirmationModalOpen,
    onClose: onApprovalConfirmationModalClose,
  } = useDisclosure();
  const {
    isOpen: isRejectConfirmationModalOpen,
    onOpen: onRejectConfirmationModalOpen,
    onClose: onRejectConfirmationModalClose,
  } = useDisclosure();

  const {
    isOpen: isRequestDetailsDrawerOpen,
    onClose: onRequestDetailsDrawerClose,
    onOpen: onRequestDetailsDrawerOpen,
  } = useDisclosure();

  const userInfo: any = useUserInfo();

  const {
    data: requestTimeOffData,
    status: requestTimeOffStatus,
    refetch,
  } = useFetchPaginatedTimeOffRequests(searchQuery);

  const columns = useMemo(
    () => [
      ...((userInfo?.roleId === roles.MANAGER || userInfo?.roleId === roles.ADMIN) && requestType === 'pending'
        ? [
            {
              id: 'selection',
              width: 40,
              Header: ({ getToggleAllRowsSelectedProps }: any) => {
                return <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} colorScheme="secondary" />;
              },
              Cell: ({ row }: any) => {
                return <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} colorScheme="secondary" />;
              },
            },
          ]
        : []),
      {
        Header: 'User Name',
        accessor: 'name',
        width: 180,
        Cell: (data: CellValue) => {
          return (
            <div>
              <Text fontWeight="semibold" color="primary.500" mb="1">
                {data.row.original?.full_name}
              </Text>
              <RequestStatus status={data.row.original?.status?.toLowerCase()} />
            </div>
          );
        },
      },

      {
        Header: 'Category',
        accessor: 'category',
        width: 180,
        Cell: (data: CellValue) => {
          const isPaid = data.row.original?.category_type?.toLowerCase() === 'paid';

          return (
            <Box>
              <Text textTransform="capitalize">{data.row.original?.category_name || '- '}</Text>
              <Badge
                mt="1"
                backgroundColor={isPaid ? 'newBlue.500' : 'gray.400'}
                fontWeight="normal"
                color="white"
                textTransform="capitalize"
              >
                {data.row.original?.category_type || '- '}
              </Badge>
            </Box>
          );
        },
      },
      {
        Header: 'Period',
        accessor: 'period',
        width: 240,
      },
      {
        Header: 'Duration (Hours)',
        accessor: 'duration',
        Cell: (data: CellValue) => {
          return <Text>{data.row.original?.request_hours}</Text>;
        },
      },
      ...(requestType !== 'pending'
        ? [
            {
              Header: 'Declined/Withdrawn Reason',
              accessor: 'reasons',
              width: 200,
              Cell: (data: CellValue) => {
                return <Text>{data.row.original?.reviewer?.comment}</Text>;
              },
            },
          ]
        : []),
      ...(userInfo?.roleId === roles.ADMIN || userInfo?.roleId === roles.MANAGER
        ? [
            {
              Header: '',
              accessor: 'action',
              width: 180,
              Cell: (data: CellValue) => {
                return (
                  <Flex alignItems="center">
                    <Tooltip label="View Requests" hasArrow bg="black">
                      <IconButton
                        aria-label="View"
                        bg="rgba(38, 103, 201, 0.1)"
                        icon={<Icon as={IconView} sx={{ '& path': { fill: 'primary.600' } }} color="primary.600" />}
                        size="sm"
                        m="1"
                        _hover={{ bg: 'rgba(38, 103, 201, 0.2)' }}
                        onClick={() => handleViewRequestDetails(data.row.original)}
                      />
                    </Tooltip>
                    {!data.row.original?.is_approve && !data.row.original?.is_reject ? (
                      <>
                        {data.row.original?.can_approve ? (
                          <Tooltip label="Approve Requests" hasArrow bg="black">
                            <IconButton
                              aria-label="Approve"
                              icon={<Icon as={IconCheckCircle} color="primary.600" fontSize="md" />}
                              size="sm"
                              m="1"
                              bg="rgba(38, 103, 201, 0.1)"
                              _hover={{ bg: 'rgba(38, 103, 201, 0.2)' }}
                              onClick={() =>
                                handleSingleApprove(data.row.original?.id, data.row.original?.category_type)
                              }
                            />
                          </Tooltip>
                        ) : null}
                        {data.row.original?.can_reject ? (
                          <Tooltip label="Decline Requests" hasArrow bg="black">
                            <IconButton
                              aria-label="Decline"
                              icon={
                                <Icon
                                  as={IoMdCloseCircle}
                                  // sx={{ '& path': { fill: 'primary.600' } }}
                                  color="newRed.1000"
                                  fontSize="md"
                                />
                              }
                              size="sm"
                              m="1"
                              bg="rgba(255, 83, 83, 0.1)"
                              _hover={{ bg: 'rgba(255, 83, 83, 0.2)' }}
                              onClick={() => handleSingleReject(data.row.original?.id)}
                            />
                          </Tooltip>
                        ) : null}
                      </>
                    ) : null}
                    {requestType === 'pending' || (data.row.original?.is_reject && !data.row.original?.is_withdrawn) ? (
                      <Tooltip label={t('timeOff:deleteRequest')} hasArrow bg="black">
                        <IconButton
                          aria-label="Delete"
                          bg="rgba(255, 83, 83, 0.1)"
                          icon={<Icon as={IconTrashbin} />}
                          size="sm"
                          _hover={{ bg: 'rgba(255, 83, 83, 0.2)' }}
                          onClick={() => handleSingleDelete(data.row.original.id, 'delete')}
                        />
                      </Tooltip>
                    ) : null}
                    {data.row.original?.is_approve && !data.row.original?.is_withdrawn ? (
                      <Tooltip label={t('timeOff:withdrawRequest')} hasArrow bg="black">
                        <IconButton
                          aria-label={t('timeOff:withdrawRequest')}
                          bg="rgba(101, 104, 134, 0.1)"
                          icon={<Icon as={IconBan} />}
                          size="sm"
                          _hover={{ bg: 'rgba(101, 104, 134, 0.2)' }}
                          onClick={() => handleSingleDelete(data.row.original.id, 'withdraw')}
                        />
                      </Tooltip>
                    ) : null}
                  </Flex>
                );
              },
            },
          ]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleEmployeeFilterFormSubmit = useCallback((selectedEmployees: any) => {
    const selectedEmployeeIds = selectedEmployees.length
      ? selectedEmployees.map((item: any) => item.userId).join(',')
      : '';

    setSearchQuery((searchQueryParam: any) => ({
      ...searchQueryParam,
      user_ids: selectedEmployeeIds,
    }));
  }, []);

  const handleCategoryChange = useCallback((values: any) => {
    const selectedCategories = values.length ? values.map((item: any) => item.id).join(',') : values?.id || '';

    setSearchQuery((searchQueryParam: any) => ({
      ...searchQueryParam,
      category_ids: selectedCategories,
    }));
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearchInputChange = useCallback(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      const searchValue = e.target.value.toLowerCase().trim();

      setSearchQuery((searchQueryParam: any) => ({
        ...searchQueryParam,
        page: 1,
        search: searchValue,
      }));
    }, 400),
    []
  );

  const handleDateChange = useCallback(
    (dates: any) => {
      const [startDate, endDate] = dates;

      if (startDate) {
        const startDateString = startDate ? format(startDate, 'yyyy-MM-dd') : null;
        const endDateString = endDate ? format(endDate, 'yyyy-MM-dd') : '';
        setSearchQuery({ ...searchQuery, page: 1, date_range: `${startDateString},${endDateString}` });
      } else {
        setSearchQuery({ ...searchQuery, page: 1, date_range: '' });
      }
    },
    [searchQuery]
  );

  const handlePageSizeChange = (pageSize: number) => {
    setSearchQuery(queries => ({ ...queries, page_size: pageSize, page: 1 }));
  };

  const handlePageChange = useCallback((selectedPageIndex: number) => {
    const updatedSearchQuery = (previousSearchQuery: ITimeOffRequestSearchQuery) => ({
      ...previousSearchQuery,
      page: selectedPageIndex + 1,
    });
    setSearchQuery(updatedSearchQuery);
  }, []);

  const handleSingleApprove = (id: number, categoryType: string) => {
    setApproveModalFormData({ ...approveModalFormData, timesheet_entry: categoryType === 'Paid' ? ['yes'] : [] });
    setDialogIds({ ids: [id] });
    onApprovalConfirmationModalOpen();
  };

  const handleBulkApprove = (selectedRows: any) => {
    let isTimesheetEntryChecked = true;

    const ids = selectedRows.map((row: any) => {
      if (isTimesheetEntryChecked && row.original?.category_type !== 'Paid') {
        isTimesheetEntryChecked = false;
      }
      return row.original.id;
    });

    setApproveModalFormData({ ...approveModalFormData, timesheet_entry: isTimesheetEntryChecked ? ['yes'] : [] });
    setDialogIds({ ids: ids });
    onApprovalConfirmationModalOpen();
  };

  const handleSingleReject = (id: number) => {
    setDialogIds({ ids: [id] });
    onRejectConfirmationModalOpen();
  };

  const handleBulkReject = (selectRows: any) => {
    const ids = selectRows.map((row: any) => row.original.id);
    setDialogIds({ ids: ids });
    onRejectConfirmationModalOpen();
  };

  const handleViewRequestDetails = (requestDetails: any) => {
    onRequestDetailsDrawerOpen();
    setRequestDetails(requestDetails);
  };

  const handleSingleDelete = (id: number, type: 'delete' | 'withdraw') => {
    setDialogIds({ ids: [id] });
    onDeleteOpen();
    confirmationModalTypeRef.current = type;
  };

  const handleBulkDelete = (selectedRows: any) => {
    const ids = selectedRows.map((row: any) => row.original.id);
    setDialogIds({ ids: ids });
    onDeleteOpen();
  };

  const handleRequestApprovalSuccess = () => {
    refetch();
    onApprovalConfirmationModalClose();
    setDialogIds({ ids: [] });
  };

  const handleRequestRejectSuccess = () => {
    refetch();
    onRejectConfirmationModalClose();
    setDialogIds({ ids: [] });
  };

  const handleRequestWithdrawSuccess = () => {
    refetch();
    onDeleteClose();
    setDialogIds({ ids: [] });
  };

  return (
    <>
      <ControllableDrawer isOpen={isRequestDetailsDrawerOpen} onClose={onRequestDetailsDrawerClose} size="lg">
        <RequestDetails
          onClose={onRequestDetailsDrawerClose}
          requestDetails={request}
          handleSingleApprove={handleSingleApprove}
          handleSingleReject={handleSingleReject}
        />
      </ControllableDrawer>

      <RequestApproveConfirmationModal
        isModelOpen={isApprovalConfirmationModalOpen}
        onModalClose={onApprovalConfirmationModalClose}
        dialogIds={dialogIds}
        handleApproveSuccess={handleRequestApprovalSuccess}
        formData={approveModalFormData}
      />

      <RequestRejectConfirmationModal
        dialogIds={dialogIds}
        isModelOpen={isRejectConfirmationModalOpen}
        onModalClose={onRejectConfirmationModalClose}
        handleRejectSuccess={handleRequestRejectSuccess}
      />

      <RequestWithdrawConfirmationModal
        dialogIds={dialogIds}
        isModelOpen={isDeleteOpen}
        onModalClose={onDeleteClose}
        type={confirmationModalTypeRef.current}
        handleRequestWithdrawSuccess={handleRequestWithdrawSuccess}
      />

      <FilterGroup
        handleCategoryChange={handleCategoryChange}
        handleDateChange={handleDateChange}
        handleSearchInputChange={handleSearchInputChange}
        searchQuery={searchQuery}
        handleEmployeeFilterFormSubmit={handleEmployeeFilterFormSubmit}
        searchPlaceholder={t('timeOff:searchRequests') + (userInfo?.roleId === 1 ? t('timeOff:orUsers') : '')}
      />

      <Table
        columns={columns}
        data={requestTimeOffData?.payload || []}
        paginationData={requestTimeOffData?.meta_data?.pagination}
        onPageChange={handlePageChange}
        handlePageSizeChange={handlePageSizeChange}
        isLoading={requestTimeOffStatus === 'loading'}
        enableRowSelect
        paginationStyle="rowsPerPage"
        emptyTableStateProps={{
          searchText: searchQuery.search,
        }}
        renderTableFooter={(selectedFlatRows: any, toggleAllRowSelected) => {
          return (
            <RequestsTableFooter
              selectedFlatRows={selectedFlatRows}
              handleBulkDelete={handleBulkDelete}
              handleBulkApprove={handleBulkApprove}
              handleBulkReject={handleBulkReject}
              toggleAllRowSelected={toggleAllRowSelected}
            />
          );
        }}
      />
    </>
  );
};
export default Requests;
